// Admin API-Keys API layer — typed wrappers over the LIVE /v3/admin/api-keys contract.
//
// ⚠️ LIVE JSON IS GROUND TRUTH (FACTS PACK §3e, probe-confirmed against v3-api-dev):
//   GET    /v3/admin/api-keys?partner=X   (partner REQUIRED, else 400) →
//            data is a BARE ARRAY of key objects. So this is SEARCH-BY-PARTNER.
//            A revoked key STAYS in the list with status:"revoked" (verified live).
//   POST   /v3/admin/api-keys  body {partner(req), company_id?, scopes?[]} →
//            data.api_key (PLAINTEXT secret "ak_..." — shown ONCE) + data.key{...}.
//   DELETE /v3/admin/api-keys/{api_key_id}  → data.status:"revoked".
// All timestamps are MICROSECONDS since epoch.

import { ApiError, apiFetch } from '../../lib/api';

export interface ApiKey {
  api_key_id: string;
  partner: string;
  company_id?: string;
  scopes?: string[];
  status?: string; // 'active' | 'revoked'
  created_at?: number; // µs
}

/** GET /v3/admin/api-keys?partner= → bare array (FACTS PACK §3e). */
export async function listApiKeys(partner: string): Promise<ApiKey[]> {
  const res = await apiFetch<ApiKey[]>('/v3/admin/api-keys', { query: { partner } });
  return Array.isArray(res.data) ? res.data : [];
}

export interface IssueApiKeyInput {
  partner: string;
  companyId?: string;
  scopes: string[];
}

export interface IssuedApiKey {
  /** the plaintext secret — shown ONCE, never retrievable again. */
  plaintext: string;
  key: ApiKey;
  requestId: string | null;
}

/** POST /v3/admin/api-keys → { data.api_key (plaintext), data.key }. */
export async function issueApiKey(input: IssueApiKeyInput): Promise<IssuedApiKey> {
  const body: Record<string, unknown> = { partner: input.partner };
  if (input.companyId) body.company_id = input.companyId;
  if (input.scopes.length > 0) body.scopes = input.scopes;
  const res = await apiFetch<{ api_key?: string; key?: ApiKey }>('/v3/admin/api-keys', { method: 'POST', body });
  const key = res.data?.key;
  return {
    plaintext: res.data?.api_key ?? '',
    key: key ?? { api_key_id: '', partner: input.partner, scopes: input.scopes, status: 'active' },
    requestId: res.requestId,
  };
}

/** DELETE /v3/admin/api-keys/{id} (revoke) → data.status. */
export async function revokeApiKey(apiKeyId: string): Promise<{ status?: string; requestId: string | null }> {
  const res = await apiFetch<{ status?: string }>(`/v3/admin/api-keys/${encodeURIComponent(apiKeyId)}`, { method: 'DELETE' });
  return { status: res.data?.status, requestId: res.requestId };
}

export function isInvalidRequest(e: unknown): boolean {
  return e instanceof ApiError && (e.status === 400 || e.code === 'INVALID_REQUEST');
}

// ---- known partner scopes (from the partner REST surface: device:read, event:read) ----
// These are the scopes the deployed partner endpoints honor (GET /v3/partner/devices
// needs device:read; /v3/partner/events needs event:read). Offered as checkboxes;
// the field also accepts free-form scopes for forward-compat.
export const KNOWN_SCOPES: { value: string; label: string }[] = [
  { value: 'device:read', label: 'device:read — 讀取裝置清單' },
  { value: 'event:read', label: 'event:read — 讀取事件' },
];

const SCOPE_LABEL: Record<string, string> = Object.fromEntries(KNOWN_SCOPES.map((s) => [s.value, s.label]));

export function scopeLabel(scope: string): string {
  return SCOPE_LABEL[scope] ?? scope;
}
